import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTripInfoContext } from '../context/TripInfoContext';
import ShadcnTripTable from './ShadcnTripTable';
import DateCarousel from './ui/DateCarousel';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/**
 * ShadcnTripInfoTable component using shadcn/ui data table
 * @param {Object} props - Component props
 * @returns {JSX.Element} ShadcnTripInfoTable component
 */
const ShadcnTripInfoTable = ({
  externalDataSourceFilter = null,
  onDataSourceFilterChange = null,
  externalSearchQuery = '',
  onSearchQueryChange = null,
  onReset = null,
  onRefresh = null,
  loading: externalLoading = false
}) => {
  const navigate = useNavigate();
  const {
    tripData,
    loading,
    error,
    pagination,
    changePage,
    departureDate,
    setDepartureDate,
    setTicketSources,
  } = useTripInfoContext();



  // Handle date change from DateCarousel (string format) to context (Date object)
  const handleDateCarouselChange = (dateString) => {
    console.log('DateCarousel date changed to:', dateString);
    if (dateString === null) {
      // Handle deselection - clear the date filter
      console.log('Clearing departure date filter');
      setDepartureDate(null);
    } else {
      const dateObject = new Date(dateString);
      console.log('Setting departure date to:', dateObject);
      setDepartureDate(dateObject);
    }
  };

  // Convert Date object to string for DateCarousel
  const getSelectedDateString = () => {
    if (!departureDate) return null;
    return dayjs(departureDate).format('YYYY-MM-DD');
  };

  // Initialize external data source filter on mount only
  useEffect(() => {
    if (externalDataSourceFilter !== null && externalDataSourceFilter.length > 0) {
      setTicketSources(externalDataSourceFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount to avoid infinite loops

  // Handle trip click
  const handleTripClick = (trip) => {
    navigate(`/check-in/${trip.ticketId}/${trip.userId}`, {
      state: {
        tripData: trip,
        activeTab: 'tripInfo'
      }
    });
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    changePage(newPage);
    // Page changes are handled automatically by useEffect in useTripInfo hook
  };



  // Initial data load is now handled in the useTripInfo hook
  // No need for manual loadTripData call here

  return (
    <div className="flex flex-col h-full w-full max-w-full overflow-hidden">
      {/* Compact Filter Bar with Date Carousel */}
      <div className="flex-shrink-0 bg-white border border-gray-200 rounded-lg p-4 shadow-sm mb-4 mx-4 mt-4">
        <div className="flex items-center gap-3">
          {/* Date Carousel - takes full available width */}
          <div className="flex-1 min-w-0">
            <DateCarousel
              selectedDate={getSelectedDateString()}
              onDateChange={handleDateCarouselChange}
              className="w-full"
            />
          </div>

          {/* Filter Button - TEMPORARILY COMMENTED OUT */}
          {/*
          <div className="flex-shrink-0 ml-auto">
            <RadixButton
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 py-2 whitespace-nowrap"
            >
              <Filter className="h-4 w-4" />
              Filters
            </RadixButton>
          </div>
          */}
        </div>
      </div>



      {/* Expandable Filter Panel - TEMPORARILY COMMENTED OUT */}
      {/*
      {showFilters && (
        <RadixCard className="sticky top-20 z-40">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-800">Advanced Filters</h3>
              <RadixButton
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(false)}
                className="text-gray-500 hover:text-gray-700 h-6 w-6 p-0"
              >
                ✕
              </RadixButton>
            </div>

            {/* Search and Filters Section - Horizontal Layout *\/}
            <div className="space-y-3">
              {/* Search Type and Input *\/}
              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-700">Search</label>
                <div className="flex flex-wrap gap-1 mb-2">
                  <RadixButton
                    variant={searchType === 'userId' ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setSearchType('userId')}
                    className="px-2 py-1 text-xs"
                  >
                    User ID
                  </RadixButton>
                  <RadixButton
                    variant={searchType === 'ticketId' ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setSearchType('ticketId')}
                    className="px-2 py-1 text-xs"
                  >
                    Ticket ID
                  </RadixButton>
                  <RadixButton
                    variant={searchType === 'email' ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setSearchType('email')}
                    className="px-2 py-1 text-xs"
                  >
                    Email
                  </RadixButton>
                  <RadixButton
                    variant={searchType === 'pnr' ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setSearchType('pnr')}
                    className="px-2 py-1 text-xs"
                  >
                    PNR
                  </RadixButton>
                </div>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                  <input
                    type={searchType === 'userId' || searchType === 'ticketId' ? 'number' : 'text'}
                    placeholder={`Search by ${searchType === 'userId' ? 'User ID' :
                               searchType === 'ticketId' ? 'Ticket ID' :
                               searchType === 'email' ? 'Email' : 'PNR'}`}
                    value={searchType === 'userId' ? userId :
                           searchType === 'ticketId' ? ticketId :
                           searchType === 'email' ? email : pnr}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (searchType === 'userId') setUserId(value);
                      else if (searchType === 'ticketId') setTicketId(value);
                      else if (searchType === 'email') setEmail(value);
                      else if (searchType === 'pnr') setPnr(value);

                      if (onSearchQueryChange) {
                        handleSearchQueryChange(value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    className="w-full pl-7 pr-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                  />
                </div>
              </div>

              {/* Filters Section - Horizontal Layout *\/}
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Data Source Filter *\/}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Data Source</label>
                    <DataSourceFilter
                      selectedSources={ticketSources}
                      onSourcesChange={handleDataSourceFilterChange}
                      size="sm"
                    />
                  </div>

                  {/* Belongs To Filter *\/}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Belongs To</label>
                    <BelongsToFilter
                      selectedBelongsTo={belongsTo}
                      onBelongsToChange={setBelongsTo}
                      size="sm"
                    />
                  </div>
                </div>

                {/* Departure Date Range Filter *\/}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-3 w-3 text-blue-600" />
                    <label className="block text-xs font-medium text-gray-700">Departure Date Range</label>
                  </div>
                  <DateRangePicker
                    value={{
                      from: departureDateMin ? new Date(departureDateMin) : undefined,
                      to: departureDateMax ? new Date(departureDateMax) : undefined
                    }}
                    onChange={(range) => {
                      const fromDate = range?.from ? range.from.toISOString().split('T')[0] : '';
                      // If only single date is selected, use it for both min and max
                      const toDate = range?.to ? range.to.toISOString().split('T')[0] : (range?.from ? range.from.toISOString().split('T')[0] : '');
                      setDepartureDateMin(fromDate);
                      setDepartureDateMax(toDate);
                    }}
                    placeholder="Select departure date range"
                    className="text-xs"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons - Compact *\/}
            <div className="flex justify-end gap-1 pt-2 border-t border-gray-100">
              <RadixButton
                variant="outline"
                size="sm"
                onClick={handleResetAll}
                className="flex items-center gap-1 px-2 py-1 text-xs"
              >
                <ResetIcon className="w-3 h-3" />
                Reset
              </RadixButton>

              <RadixButton
                variant="outline"
                size="sm"
                onClick={handleSearch}
                className="flex items-center gap-1 px-2 py-1 text-xs"
              >
                <MagnifyingGlassIcon className="w-3 h-3" />
                Search
              </RadixButton>

              <RadixButton
                variant="primary"
                size="sm"
                onClick={handleRefreshData}
                className="flex items-center gap-1 px-2 py-1 text-xs"
                disabled={externalLoading || loading}
              >
                <ReloadIcon className={`w-3 h-3 ${(externalLoading || loading) ? 'animate-spin' : ''}`} />
                Refresh
              </RadixButton>
            </div>
          </div>
        </RadixCard>
      )}
      */}

      {/* Active Filters Display - TEMPORARILY COMMENTED OUT */}
      {/*
      {(departureDate || departureDateMin || departureDateMax || userId || ticketId || email || pnr || (ticketSources && ticketSources.length > 0) || (belongsTo && belongsTo !== 'all')) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg">
          {/* Header with toggle *\/}
          <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => setShowActiveFilters(!showActiveFilters)}>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-blue-800">Active filters</span>
              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                {[
                  departureDate ? 1 : 0,
                  departureDateMin ? 1 : 0,
                  departureDateMax ? 1 : 0,
                  userId ? 1 : 0,
                  ticketId ? 1 : 0,
                  email ? 1 : 0,
                  pnr ? 1 : 0,
                  (ticketSources && ticketSources.length > 0) ? 1 : 0,
                  (belongsTo && belongsTo !== 'all') ? 1 : 0
                ].reduce((sum, val) => sum + val, 0)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <RadixButton
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleResetAll();
                }}
                className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1"
              >
                Clear all
              </RadixButton>
              <span className="text-blue-600">
                {showActiveFilters ? '▼' : '▶'}
              </span>
            </div>
          </div>

          {/* Collapsible content *\/}
          {showActiveFilters && (
            <div className="px-3 pb-3">
              <div className="flex flex-wrap gap-2">
                {/* Departure Date Filter *\/}
                {departureDate && (
                  <div className="flex items-center gap-1 bg-white border border-blue-300 rounded-md px-2 py-1">
                    <span className="text-xs text-blue-800">
                      Departure: {dayjs(departureDate).format('MMM DD, YYYY')}
                    </span>
                    <button
                      onClick={() => {
                        setDepartureDate(null);
                        // API will be called automatically via useEffect in useTripInfo hook
                      }}
                      className="text-blue-600 hover:text-blue-800 ml-1"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Departure Date Range Filter - Split into two separate chips *\/}
                {departureDateMin && (
                  <div className="flex items-center gap-1 bg-white border border-blue-300 rounded-md px-2 py-1">
                    <span className="text-xs text-blue-800">
                      Departure From: {dayjs(departureDateMin).format('MMM DD, YYYY')}
                    </span>
                    <button
                      onClick={() => {
                        setDepartureDateMin('');
                        // API will be called automatically via useEffect in useTripInfo hook
                      }}
                      className="text-blue-600 hover:text-blue-800 ml-1"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {departureDateMax && (
                  <div className="flex items-center gap-1 bg-white border border-blue-300 rounded-md px-2 py-1">
                    <span className="text-xs text-blue-800">
                      Departure To: {dayjs(departureDateMax).format('MMM DD, YYYY')}
                    </span>
                    <button
                      onClick={() => {
                        setDepartureDateMax('');
                        // API will be called automatically via useEffect in useTripInfo hook
                      }}
                      className="text-blue-600 hover:text-blue-800 ml-1"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* User ID Filter *\/}
                {userId && (
                  <div className="flex items-center gap-1 bg-white border border-blue-300 rounded-md px-2 py-1">
                    <span className="text-xs text-blue-800">User ID: {userId}</span>
                    <button
                      onClick={() => {
                        setUserId('');
                        // API will be called automatically via useEffect in useTripInfo hook
                      }}
                      className="text-blue-600 hover:text-blue-800 ml-1"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Ticket ID Filter *\/}
                {ticketId && (
                  <div className="flex items-center gap-1 bg-white border border-blue-300 rounded-md px-2 py-1">
                    <span className="text-xs text-blue-800">Ticket ID: {ticketId}</span>
                    <button
                      onClick={() => {
                        setTicketId('');
                        // API will be called automatically via useEffect in useTripInfo hook
                      }}
                      className="text-blue-600 hover:text-blue-800 ml-1"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Email Filter *\/}
                {email && (
                  <div className="flex items-center gap-1 bg-white border border-blue-300 rounded-md px-2 py-1">
                    <span className="text-xs text-blue-800">Email: {email}</span>
                    <button
                      onClick={() => {
                        setEmail('');
                        // API will be called automatically via useEffect in useTripInfo hook
                      }}
                      className="text-blue-600 hover:text-blue-800 ml-1"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* PNR Filter *\/}
                {pnr && (
                  <div className="flex items-center gap-1 bg-white border border-blue-300 rounded-md px-2 py-1">
                    <span className="text-xs text-blue-800">PNR: {pnr}</span>
                    <button
                      onClick={() => {
                        setPnr('');
                        // API will be called automatically via useEffect in useTripInfo hook
                      }}
                      className="text-blue-600 hover:text-blue-800 ml-1"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Ticket Sources Filter *\/}
                {ticketSources && ticketSources.length > 0 && (
                  <div className="flex items-center gap-1 bg-white border border-blue-300 rounded-md px-2 py-1">
                    <span className="text-xs text-blue-800">
                      Sources: {ticketSources.join(', ')}
                    </span>
                    <button
                      onClick={() => {
                        setTicketSources([]);
                        // API will be called automatically via useEffect in useTripInfo hook
                      }}
                      className="text-blue-600 hover:text-blue-800 ml-1"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Belongs To Filter *\/}
                {belongsTo && belongsTo !== 'all' && (
                  <div className="flex items-center gap-1 bg-white border border-blue-300 rounded-md px-2 py-1">
                    <span className="text-xs text-blue-800">Belongs To: {belongsTo}</span>
                    <button
                      onClick={() => {
                        setBelongsTo('all');
                        // API will be called automatically via useEffect in useTripInfo hook
                      }}
                      className="text-blue-600 hover:text-blue-800 ml-1"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      */}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800 text-sm">{error}</div>
        </div>
      )}

      {/* Trip Table Container with controlled height */}
      <div className="flex-1 min-h-0 px-4 pb-4">
        <ShadcnTripTable
          trips={tripData}
          onTripClick={handleTripClick}
          pagination={{
            page: pagination.pageNumber,
            totalPages: Math.ceil(tripData.length / pagination.itemsPerPage),
            limit: pagination.itemsPerPage
          }}
          onPageChange={handlePageChange}
          loading={externalLoading || loading}
        />
      </div>
    </div>
  );
};

export default ShadcnTripInfoTable;
